import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { getDiscordAvatarUrl } from "../utils/discord";
import ProfilePortal from "./ProfilePortal";
import OverviewActions from "./OverviewActions";
import ShopPage from "./ShopPage";
import BadgesPage from "./BadgesPage";
import FamilyPage from "./FamilyPage";
import GlobalPage from "./GlobalPage";

const API_BASE = process.env.REACT_APP_API_BASE;

const PROFILE_TABS = [
  { id: "overview", label: "Overview", public: false },
  { id: "customize", label: "Customize", public: false },
  { id: "shop", label: "Shop", public: false },
  { id: "badges", label: "Badges", public: true },
  { id: "family", label: "Family", public: true },
  { id: "global", label: "Global", public: true },
  { id: "boosters", label: "Boosters", public: false },
];

const PUBLIC_TAB_IDS = new Set(
  PROFILE_TABS.filter((t) => t.public).map((t) => t.id),
);

function normalizeTab(raw, { allowPrivate }) {
  const id = String(raw || "").toLowerCase();
  const match = PROFILE_TABS.find((t) => t.id === id);
  if (!match) return allowPrivate ? "overview" : "global";
  if (!allowPrivate && !match.public) return "global";
  return match.id;
}

function loginGate({ title, body }) {
  return (
    <div className="profile-login-gate">
      <h1>{title}</h1>
      <p>{body}</p>
      <button
        type="button"
        className="nav-login-btn"
        onClick={() => {
          window.location.href = `${API_BASE}/auth/login`;
        }}
      >
        Log in with Discord
      </button>
    </div>
  );
}

function guildIconUrl(guild) {
  if (!guild?.guildId || !guild?.guildIcon) return null;
  return `https://cdn.discordapp.com/icons/${guild.guildId}/${guild.guildIcon}.png?size=64`;
}

function BoosterRoleEditor({ entry, token, onUpdated }) {
  const existingRole = entry.role || (entry.roleId ? { id: entry.roleId } : null);
  const [name, setName] = useState(entry.role?.name || "");
  const [color, setColor] = useState(entry.role?.color || "#4a0e17");
  const [icon, setIcon] = useState(entry.role?.unicodeEmoji || "");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setName(entry.role?.name || "");
    setColor(entry.role?.color || "#4a0e17");
    setIcon(entry.role?.unicodeEmoji || "");
    setMessage(null);
    setError(null);
  }, [
    entry.guildId,
    entry.roleId,
    entry.role?.id,
    entry.role?.name,
    entry.role?.color,
    entry.role?.unicodeEmoji,
  ]);

  const save = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const body = {};
      if (name.trim()) body.name = name.trim();
      if (color.trim()) body.color = color.trim();
      if (icon.trim()) body.icon = icon.trim();
      await axios.patch(`${API_BASE}/me/booster-role/${entry.guildId}`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage(existingRole ? "Updated." : "Created.");
      await onUpdated();
    } catch (err) {
      setError(err.response?.data?.error || "Could not save booster role.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!existingRole) return;
    if (
      !window.confirm(
        `Delete your custom booster role in ${entry.guildName}?`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await axios.delete(`${API_BASE}/me/booster-role/${entry.guildId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage("Deleted.");
      setName("");
      setIcon("");
      await onUpdated();
    } catch (err) {
      setError(err.response?.data?.error || "Could not delete booster role.");
    } finally {
      setBusy(false);
    }
  };

  const previewColor = (() => {
    const m = String(color || "").match(/#?[0-9a-fA-F]{6}/);
    if (!m) return "#4a0e17";
    return m[0].startsWith("#") ? m[0] : `#${m[0]}`;
  })();

  return (
    <div className={`profile-booster-item${entry.eligible ? "" : " locked"}`}>
      <div className="profile-booster-head">
        {guildIconUrl(entry) ? (
          <img
            src={guildIconUrl(entry)}
            alt=""
            className="profile-booster-icon"
          />
        ) : (
          <div className="profile-booster-icon placeholder" />
        )}
        <div>
          <h3>{entry.guildName}</h3>
          {!entry.eligible && (
            <p className="profile-booster-reason">
              {entry.reason ||
                "Custom booster roles unlock when you boost Nocturne, or when this server has local booster roles enabled and you boost here."}
            </p>
          )}
          {entry.eligible && existingRole && entry.role?.unresolved && (
            <p className="profile-booster-reason">
              Your booster role is saved - Discord details couldn&apos;t load.
              Enter the name/colour and hit Save to update it.
            </p>
          )}
        </div>
        {entry.role?.name && (
          <span
            className="profile-role-swatch"
            style={{ background: previewColor }}
            title={entry.role.name}
          />
        )}
      </div>

      {entry.eligible ? (
        <div className="profile-booster-form">
          <label>
            Role name
            <input
              type="text"
              value={name}
              maxLength={100}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your role name"
              disabled={busy}
            />
          </label>
          <label>
            Colour
            <input
              type="text"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              placeholder="#4a0e17 or #ff0000, #00ff00"
              disabled={busy}
            />
          </label>
          <label>
            Icon (optional)
            <input
              type="text"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              placeholder="Emoji or <:name:id>"
              disabled={busy}
            />
          </label>
          <div className="profile-booster-actions">
            <button
              type="button"
              className="profile-btn"
              onClick={save}
              disabled={busy || (!existingRole && !name.trim())}
            >
              {busy ? "Saving…" : existingRole ? "Save" : "Create"}
            </button>
            {existingRole && (
              <button
                type="button"
                className="profile-btn danger"
                onClick={remove}
                disabled={busy}
              >
                Delete
              </button>
            )}
          </div>
          {message && <p className="profile-booster-ok">{message}</p>}
          {error && <p className="profile-booster-err">{error}</p>}
        </div>
      ) : existingRole ? (
        <p className="profile-booster-current">
          Current role:{" "}
          <strong>{entry.role?.name || "Custom booster"}</strong>
        </p>
      ) : null}
    </div>
  );
}

export default function ProfilePage({ user, isOwn }) {
  const { userId: routeUserId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewedId = isOwn ? user?.id : routeUserId;
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [badgesOpen, setBadgesOpen] = useState(false);

  const token =
    typeof window !== "undefined" ? localStorage.getItem("kami_token") : null;
  const loggedIn = Boolean(user?.id);
  const allowPrivate = Boolean(isOwn && loggedIn);
  const showTabs = Boolean(isOwn) || Boolean(routeUserId);
  const visibleTabs = PROFILE_TABS.filter((tab) => {
    if (tab.public || tab.id === "overview") return true;
    if (!isOwn) return false;
    return loggedIn;
  });

  const activeTab = showTabs
    ? normalizeTab(searchParams.get("tab"), {
        allowPrivate:
          allowPrivate ||
          (!isOwn && (PUBLIC_TAB_IDS.has(searchParams.get("tab")) || !searchParams.get("tab"))),
      })
    : "overview";

  const guestOnProfile = Boolean(isOwn && !loggedIn);
  const resolvedTab = (() => {
    if (!guestOnProfile) {
      // Public profiles: allow overview + public tabs only.
      if (!isOwn && !PUBLIC_TAB_IDS.has(activeTab) && activeTab !== "overview") {
        return "overview";
      }
      return activeTab;
    }
    if (!searchParams.get("tab")) return "global";
    if (PUBLIC_TAB_IDS.has(activeTab) || activeTab === "overview") return activeTab;
    return "global";
  })();

  const setTab = (id) => {
    const next = normalizeTab(id, {
      allowPrivate: true,
    });
    const allowed =
      next === "overview" ||
      PUBLIC_TAB_IDS.has(next) ||
      (isOwn && loggedIn);
    const tab = allowed ? next : "global";
    if ((isOwn && loggedIn && tab === "overview") || (!isOwn && tab === "overview")) {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ tab }, { replace: true });
    }
  };

  const load = useCallback(async () => {
    if (isOwn && !user?.id) {
      setProfile(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isOwn) {
        if (!user?.id) {
          setProfile(null);
          return;
        }

        if (token) {
          try {
            const { data } = await axios.get(`${API_BASE}/me/profile`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            setProfile(data);
            if (data.memberGuilds) {
              localStorage.setItem(
                "kami_member_guilds",
                JSON.stringify(data.memberGuilds),
              );
            }
            return;
          } catch (err) {
            const status = err.response?.status;
            if (status === 401) {
              localStorage.removeItem("kami_token");
            }
          }
        }

        const { data } = await axios.get(`${API_BASE}/profile/${user.id}`);
        setProfile({ ...data, boosterRoles: [] });
      } else {
        if (!viewedId) {
          setProfile(null);
          setError("Missing user id.");
          return;
        }
        const { data } = await axios.get(`${API_BASE}/profile/${viewedId}`);
        setProfile(data);
      }
    } catch (err) {
      setError(err.response?.data?.error || "Could not load profile.");
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [isOwn, token, user?.id, viewedId]);

  useEffect(() => {
    load();
  }, [load]);

  const renderPublicTab = () => {
    if (resolvedTab === "badges") return <BadgesPage user={user} embedded />;
    if (resolvedTab === "family") {
      return (
        <FamilyPage
          user={user}
          embedded
          targetUserId={!isOwn ? viewedId : undefined}
          allowPublicBrowse={guestOnProfile || !user?.id}
        />
      );
    }
    if (resolvedTab === "global") return <GlobalPage embedded />;
    return null;
  };

  // Logged-out Profile hub: public pages stay open.
  if (guestOnProfile) {
    return (
      <div className="profile-page">
        <nav className="profile-hub-tabs" aria-label="Profile sections">
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`profile-hub-tab${resolvedTab === tab.id ? " active" : ""}`}
              onClick={() => setTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
        {resolvedTab === "overview"
          ? loginGate({
              title: "Your profile",
              body: "Log in with Discord to view your level, companions, shop, and booster roles. Badges, Family, and Global stay open to everyone.",
            })
          : renderPublicTab()}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="profile-page">
        <div className="page-loading">
          <div className="sync-spinner" />
        </div>
      </div>
    );
  }

  if ((error || !profile) && resolvedTab === "overview") {
    return (
      <div className="profile-page">
        {showTabs && (
          <nav className="profile-hub-tabs" aria-label="Profile sections">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`profile-hub-tab${resolvedTab === tab.id ? " active" : ""}`}
                onClick={() => setTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        )}
        <div className="profile-login-gate">
          <h1>Profile</h1>
          <p>{error || "Profile not found."}</p>
          {isOwn && (
            <button
              type="button"
              className="nav-login-btn"
              style={{ marginBottom: 16 }}
              onClick={() => {
                window.location.href = `${API_BASE}/auth/login`;
              }}
            >
              Log in with Discord
            </button>
          )}
          <Link to="/">Back home</Link>
        </div>
      </div>
    );
  }

  // Public profile can open Badges / Family / Global even if overview fails.
  if ((error || !profile) && PUBLIC_TAB_IDS.has(resolvedTab)) {
    return (
      <div className="profile-page">
        {showTabs && (
          <nav className="profile-hub-tabs" aria-label="Profile sections">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`profile-hub-tab${resolvedTab === tab.id ? " active" : ""}`}
                onClick={() => setTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        )}
        {renderPublicTab()}
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="profile-page">
        <div className="profile-login-gate">
          <h1>Profile</h1>
          <p>{error || "Profile not found."}</p>
          <Link to="/">Back home</Link>
        </div>
      </div>
    );
  }

  const xpPct = Math.min(
    100,
    Math.round(
      ((Number(profile.xp) || 0) /
        Math.max(1, Number(profile.xpForNext) || 1)) *
        100,
    ),
  );
  const accent = profile.accentColor || "#4a0e17";
  const avatar =
    profile.avatarUrl ||
    getDiscordAvatarUrl({ id: profile.userId, avatar: null });
  const canEditBoosters = Boolean(isOwn && token);

  const overview = (
    <>
      <section
        className="profile-hero"
        style={{ "--profile-accent": accent }}
      >
        <img src={avatar} alt="" className="profile-avatar" />
        <div className="profile-identity">
          <p className="profile-title">{profile.activeTitle || "Seeker"}</p>
          <h1>{profile.username}</h1>
          <p className="profile-meta">
            Level {profile.level}
            {profile.hasPatron ? " · Patron" : ""}
          </p>
        </div>
      </section>

      <section className="profile-stats">
        <div className="profile-xp">
          <div className="profile-xp-labels">
            <span>Experience</span>
            <span>
              {(profile.xp || 0).toLocaleString()} /{" "}
              {(profile.xpForNext || 0).toLocaleString()}
            </span>
          </div>
          <div className="profile-xp-track">
            <div className="profile-xp-fill" style={{ width: `${xpPct}%` }} />
          </div>
        </div>

        <div className="profile-stat-grid">
          <div>
            <span className="label">Essence</span>
            <strong>{(profile.essence || 0).toLocaleString()}</strong>
          </div>
          <div>
            <span className="label">Quests</span>
            <strong>{(profile.questsFinished || 0).toLocaleString()}</strong>
          </div>
          <div>
            <span className="label">Companion</span>
            <strong>{profile.pet?.display || "None"}</strong>
          </div>
          <div>
            <span className="label">Badges</span>
            <strong>{profile.badgeCount || 0}</strong>
          </div>
        </div>

        {(profile.badges || []).length > 0 && (
          <div className="profile-badges">
            <button
              type="button"
              className="profile-badges-toggle"
              onClick={() => setBadgesOpen((o) => !o)}
            >
              {badgesOpen ? "Hide badges" : "Show badges"}
            </button>
            {badgesOpen && (
              <ul>
                {profile.badges.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      {isOwn && token && (
        <OverviewActions
          profile={profile}
          token={token}
          onPortalUpdate={(portal) =>
            setProfile((prev) => {
              const mergedActions = { ...(prev.actions || {}) };
              for (const [key, next] of Object.entries(portal.actions || {})) {
                const curr = mergedActions[key];
                // Never let a later response revive an action that's still cooling down
                // (unless the client explicitly force-unlocks after a failed request).
                if (
                  curr &&
                  curr.ready === false &&
                  (curr.remainingMs || 0) > 5_000 &&
                  next?.ready === true &&
                  !next.forceReady
                ) {
                  continue;
                }
                if (
                  curr?.ready === false &&
                  next?.ready === false &&
                  (Number(curr.remainingMs) || 0) >
                    (Number(next.remainingMs) || 0)
                ) {
                  continue;
                }
                const { forceReady: _drop, ...clean } = next || {};
                mergedActions[key] = clean;
              }
              const nextDaily = portal.daily || prev.daily;
              const keepDaily =
                prev.daily?.ready === false &&
                (prev.daily?.remainingMs || 0) > 5_000 &&
                nextDaily?.ready === true &&
                !nextDaily?.forceReady;
              const daily = keepDaily ? prev.daily : nextDaily;
              if (daily && daily.forceReady) {
                const { forceReady: _d, ...cleanDaily } = daily;
                return {
                  ...prev,
                  ...portal,
                  actions: mergedActions,
                  daily: cleanDaily,
                };
              }
              return {
                ...prev,
                ...portal,
                actions: mergedActions,
                daily,
              };
            })
          }
        />
      )}
      {isOwn && !token && loggedIn && (
        <p className="profile-empty">
          Log in again to use daily, work, explore, and companion actions here.
        </p>
      )}
    </>
  );

  const boosters = isOwn && (
    <section className="profile-boosters">
      <h2>Booster roles</h2>
      <p className="profile-section-help">
        Edit your custom booster role in servers where you qualify. Share
        invites stay in Discord via <code>/level booster share</code>.
      </p>
      {!canEditBoosters ? (
        <p className="profile-empty">
          Log in again to manage booster roles from the site.
        </p>
      ) : (profile.boosterRoles || []).length === 0 ? (
        <p className="profile-empty">
          No eligible servers yet. Boost Nocturne for perks everywhere, or
          boost a server that allows local custom booster roles.
        </p>
      ) : (
        profile.boosterRoles.map((entry) => (
          <BoosterRoleEditor
            key={entry.guildId}
            entry={entry}
            token={token}
            onUpdated={load}
          />
        ))
      )}
    </section>
  );

  return (
    <div className="profile-page">
      {!token && isOwn && loggedIn && (
        <div className="profile-session-banner">
          Session expired.{" "}
          <button
            type="button"
            className="linkish"
            onClick={() => {
              window.location.href = `${API_BASE}/auth/login`;
            }}
          >
            Log in again
          </button>{" "}
          to edit booster roles.
        </div>
      )}

      {showTabs && (
        <nav className="profile-hub-tabs" aria-label="Profile sections">
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`profile-hub-tab${resolvedTab === tab.id ? " active" : ""}`}
              onClick={() => setTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      )}

      {resolvedTab === "overview" && overview}

      {resolvedTab === "customize" && token && (
        <ProfilePortal
          profile={profile}
          token={token}
          onPortalUpdate={(portal) =>
            setProfile((prev) => ({ ...prev, ...portal }))
          }
        />
      )}
      {resolvedTab === "customize" && !token && (
        <p className="profile-empty">Log in again to customize your profile.</p>
      )}

      {resolvedTab === "shop" && (
        <ShopPage user={user} embedded initialPortal={profile} />
      )}
      {resolvedTab === "badges" && <BadgesPage user={user} embedded />}
      {resolvedTab === "family" && (
        <FamilyPage
          user={user}
          embedded
          targetUserId={!isOwn ? viewedId : undefined}
          allowPublicBrowse
        />
      )}
      {resolvedTab === "global" && <GlobalPage embedded />}
      {resolvedTab === "boosters" && boosters}

      {!isOwn && user?.id === profile.userId && (
        <p className="profile-own-link">
          <Link to="/profile">Manage your profile</Link>
        </p>
      )}
    </div>
  );
}
